import React, { useEffect, useRef, useState } from 'react';
import { 
  FaBullseye, FaEye, FaStar, FaHeart, FaCrosshairs,
  FaLightbulb, FaHandHoldingHeart, FaGlobe, FaBookOpen,
  FaUserGraduate, FaChalkboardTeacher, FaCheckCircle,
  FaArrowRight, FaGem, FaLeaf, FaShieldAlt, FaBullhorn,
  FaRocket, FaChartLine, FaUsers, FaTrophy
} from 'react-icons/fa';

const MissionVisionPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const coreValues = [
    { icon: <FaGem />, title: "Excellence", desc: "Striving for the highest standards in education", color: "from-yellow-400 to-yellow-500" },
    { icon: <FaLeaf />, title: "Integrity", desc: "Upholding honesty and ethical practices", color: "from-green-400 to-green-500" },
    { icon: <FaHandHoldingHeart />, title: "Compassion", desc: "Caring for each student's unique needs", color: "from-pink-400 to-pink-500" },
    { icon: <FaShieldAlt />, title: "Empowerment", desc: "Enabling students to reach their full potential", color: "from-purple-400 to-purple-500" }
  ];

  const goals = [
    "Provide quality higher education to women",
    "Foster academic excellence and research",
    "Develop leadership and communication skills",
    "Promote cultural and social awareness",
    "Create industry-ready professionals",
    "Build confidence and self-reliance"
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-yellow-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
            <div className="w-8 h-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
            <div className="w-6 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-orange-500 font-semibold">Mission & Vision</span>
          </div>

          {/* Heading */}
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-bold mb-3">
              <span className="bg-gradient-to-r from-yellow-500 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                Mission & Vision
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4 max-w-2xl mx-auto">
              Our guiding principles and commitment to excellence in education
            </p>
          </div>

          {/* Mission and Vision Cards */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            
            {/* Mission Card */}
            <div className={`bg-white rounded-2xl p-8 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border-l-8 border-yellow-400 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-100'
            }`} style={{ transitionDelay: '100ms' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <FaBullseye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                To empower women through quality education, fostering academic excellence, ethical values, 
                and holistic development for a brighter future. We strive to create an inclusive learning 
                environment that nurtures talent, builds character, and prepares students for the challenges 
                of tomorrow.
              </p>
              <div className="bg-yellow-50 rounded-xl pt-4 px-4 pb-2 mt-4">
                <p className="text-yellow-600 text-sm font-semibold mb-2"> Key Focus Areas:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-yellow-100 text-yellow-700 text-xs px-3 py-1 rounded-full">Quality Education</span>
                  <span className="bg-yellow-100 text-yellow-700 text-xs px-3 py-1 rounded-full">Women Empowerment</span>
                  <span className="bg-yellow-100 text-yellow-700 text-xs px-3 py-1 rounded-full">Holistic Development</span>
                </div>
              </div>
            </div>

            {/* Vision Card */}
            <div className={`bg-white rounded-2xl p-8 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border-r-8 border-blue-400 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-100'
            }`} style={{ transitionDelay: '200ms' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <FaEye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                To be a premier institution nurturing empowered, confident, and socially responsible 
                women leaders contributing to national development and global progress through excellence 
                in education. We envision a future where every woman student becomes a catalyst for 
                positive change in society.
              </p>
              <div className="bg-blue-50 rounded-xl pt-4 px-4 pb-2 mt-4">
                <p className="text-blue-600 text-sm font-semibold mb-2"> Future Goals:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full">Global Recognition</span>
                  <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full">Research Excellence</span>
                  <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full">Social Impact</span>
                </div>
              </div>
            </div>
          </div>
          {/* Core Values Section */}
          <div className={`mb-16 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-100'
          }`} style={{ transitionDelay: '300ms' }}>
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Our Core Values</h3>
              <div className="w-20 h-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mx-auto"></div>
              <p className="text-gray-500 text-sm mt-3">The principles that guide everything we do</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreValues.map((value, idx) => (
                <div key={idx} className="group bg-white rounded-xl p-6 text-center shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${value.color} flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <div className="text-white text-2xl">{value.icon}</div>
                  </div>
                  <h4 className="text-gray-800 font-bold text-lg mb-2">{value.title}</h4>
                  <p className="text-gray-500 text-xs">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Goals Section */}
          <div className={`grid lg:grid-cols-2 gap-8 mb-16 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-100'
          }`} style={{ transitionDelay: '400ms' }}>
            
            {/* Left Side - Goals List */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-2 mb-5">
                <FaCrosshairs className="text-orange-500 text-2xl" />
                <h3 className="text-xl font-bold text-gray-800">Strategic Goals</h3>
              </div>
              <div className="space-y-3">
                {goals.map((goal, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white rounded-lg p-3 hover:shadow-md transition-all">
                    <FaCheckCircle className="text-green-500 text-sm mt-0.5" />
                    <span className="text-gray-700 text-sm">{goal}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Quote/Message */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-2 mb-5">
                <FaLightbulb className="text-yellow-500 text-2xl" />
                <h3 className="text-xl font-bold text-gray-800">Message from Administration</h3>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-inner">
                <p className="text-gray-600 text-sm italic leading-relaxed">
                  "At BNS Mahila Maha Vidyalaya, we believe that education is not just about acquiring knowledge, 
                  but about transforming lives. Our mission and vision reflect our unwavering commitment to 
                  empowering women through quality education and holistic development."
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="w-10 h-0.5 bg-yellow-400"></div>
                  <p className="text-orange-600 text-xs font-semibold">- College Administration</p>
                </div>
              </div>
            </div>
          </div>

          {/* Commitment Banner */}
          <div className={`bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-2xl p-8 mb-10 transition-all duration-500 hover:shadow-2xl ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-100'
          }`} style={{ transitionDelay: '500ms' }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <FaHandHoldingHeart className="text-white text-3xl" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-xl">Our Commitment</h4>
                  <p className="text-white/90 text-sm">To create future leaders through quality education</p>
                </div>
              </div>
              <button className="bg-white text-orange-600 px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-100 transition-all duration-300 flex items-center gap-2 hover:scale-105 shadow-lg">
                Join Our Journey
                <FaArrowRight className="text-sm" />
              </button>
            </div>
          </div>

          {/* Bottom Decorative */}
          <div className="flex justify-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default MissionVisionPage;